export interface ErrorReport {
  readonly error: Error;
  readonly componentStack: string | null;
  /** Where it happened — route path, run id, whatever the caller knows. */
  readonly context?: Readonly<Record<string, string>> | undefined;
}

export type ErrorReporter = (report: ErrorReport) => void;

/**
 * Normalise whatever was thrown into an Error.
 *
 * JavaScript permits throwing anything, and a `throw 'boom'` reaching the
 * boundary would otherwise produce a report with no stack and no message.
 */
export function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  return new Error(`Non-Error thrown: ${JSON.stringify(value)}`);
}

const consoleReporter: ErrorReporter = ({ error, componentStack }) => {
  console.error(error, componentStack);
};

let current: ErrorReporter = consoleReporter;

/**
 * Install a reporter. P0-INF-11 calls this once from main.tsx with a
 * Sentry-backed implementation.
 *
 * A port rather than an @sentry/react import because P0-WEB-01 does not depend
 * on P0-INF-11 — the boundary has to work, and be testable, before any DSN
 * exists. Swapping the implementation later touches this file and nothing else.
 */
export function setErrorReporter(reporter: ErrorReporter): void {
  current = reporter;
}

/** Restores the console default. Tests use it; nothing in the app should. */
export function resetErrorReporter(): void {
  current = consoleReporter;
}

export function reportError(report: ErrorReport): void {
  try {
    current(report);
  } catch {
    // A reporter that throws must never replace the error it was handed —
    // that turns one diagnosable failure into two undiagnosable ones.
  }
}
